import { Store } from '@ngrx/store';
import * as AuthActions from './auth.actions';
import { selectAuth } from './auth.selectors';
import { AuthState, initialAuthState } from './auth.state';

/**
 * Synchronous access to the login session held in the NgRx store.
 *
 * The store is the only place the token, user and selected agency live. These helpers are
 * drop-in replacements for the old `localStorage.getItem/setItem/removeItem('token' | 'user' |
 * 'selectedAgency')` calls, with the same value shapes (token as a string; user and agency as
 * JSON strings), so guards, interceptors and getters keep working unchanged.
 *
 * Persistence: the store is mirrored to sessionStorage (one key, SESSION_KEY) so a page refresh
 * keeps the user signed in. sessionStorage belongs to a single browser tab and is wiped when the
 * tab is closed, so the session does not outlive the tab and is not shared with other tabs.
 * Nothing is written to localStorage.
 *
 * For new code prefer the selectors in auth.selectors.ts (store.select(selectUser) etc.).
 */
export type AuthKey = 'token' | 'user' | 'selectedAgency';

const LEGACY_KEYS: AuthKey[] = ['token', 'user', 'selectedAgency'];
const SESSION_KEY = 'pouchSession';

let store: Store<any> | null = null;
let snapshot: AuthState = { ...initialAuthState };

/**
 * Called once at app start-up (see app.config.ts), before the first route guard runs:
 * restores the session saved in this tab, then keeps sessionStorage in sync with the store.
 */
export function connectAuthSession(appStore: Store<any>): void {
  store = appStore;

  // 1) Restore this tab's session (survives refresh, not closing the tab)
  const saved = readSavedSession();
  if (saved) {
    snapshot = saved;
    appStore.dispatch(AuthActions.setToken({ token: saved.token }));
    appStore.dispatch(AuthActions.setUser({ user: saved.user }));
    appStore.dispatch(AuthActions.setSelectedAgency({ agency: saved.selectedAgency }));
  }

  // 2) Keep the snapshot and sessionStorage in sync with the store
  appStore.select(selectAuth).subscribe(state => {
    if (!state) return;
    snapshot = state;
    saveSession(state);
  });

  // Remove copies saved by older versions of the app
  try {
    LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
  } catch { }
}

function readSavedSession(): AuthState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      token: typeof parsed.token === 'string' ? parsed.token : null,
      user: parsed.user ?? null,
      selectedAgency: parsed.selectedAgency ?? null,
    };
  } catch {
    return null;
  }
}

function saveSession(state: AuthState): void {
  try {
    if (state.token || state.user || state.selectedAgency) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        token: state.token,
        user: state.user,
        selectedAgency: state.selectedAgency,
      }));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // Storage blocked (private mode etc.): the session still works until refresh
  }
}

/** Same result shape as localStorage.getItem(key). */
export function getAuthItem(key: AuthKey): string | null {
  const value = key === 'selectedAgency' ? snapshot.selectedAgency : snapshot[key];
  if (value === null || value === undefined) return null;
  return key === 'token' ? String(value) : JSON.stringify(value);
}

/** Parsed value (object for user / selectedAgency). */
export function getAuthValue<T = any>(key: AuthKey): T | null {
  return (snapshot[key] ?? null) as T | null;
}

/** Same arguments as localStorage.setItem(key, value). */
export function setAuthItem(key: AuthKey, value: string | any): void {
  let parsed: any = value;
  if (key !== 'token' && typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = null;
    }
  }
  if (key === 'token') {
    snapshot = { ...snapshot, token: parsed ?? null };
    store?.dispatch(AuthActions.setToken({ token: parsed ?? null }));
  } else if (key === 'user') {
    snapshot = { ...snapshot, user: parsed };
    store?.dispatch(AuthActions.setUser({ user: parsed }));
  } else {
    snapshot = { ...snapshot, selectedAgency: parsed };
    store?.dispatch(AuthActions.setSelectedAgency({ agency: parsed }));
  }
}

/** Same as localStorage.removeItem(key). */
export function removeAuthItem(key: AuthKey): void {
  setAuthItem(key, key === 'token' ? null : 'null');
}

/** Logout / session expired: forget the whole session (store and this tab's sessionStorage). */
export function clearSession(): void {
  snapshot = { ...initialAuthState };
  store?.dispatch(AuthActions.clearSession());
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch { }
}
